<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { DropdownMenuSize } from './DropdownMenu.types';
  import { MENU_HEIGHTS, MENU_SIZE } from './lib';
  import { Menu, Floating, FloatingPlacement, FloatingTrigger, type MenuOption } from '@/components';

  interface DropdownMenuProps {
    options: MenuOption[];
    size?: `${DropdownMenuSize}`;
    trigger?: `${FloatingTrigger}`;
    placement?: `${FloatingPlacement}`;
    visible?: boolean;
    closable?: boolean;
    fullWidth?: boolean;
    textKey?: string;
    valueKey?: string;
    width?: string;
    persistent?: boolean;
  }

  interface DropdownMenuEmits {
    (e: 'option-click', value: MenuOption['value']): void;
    (e: 'change-visible', value: boolean): void;
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<DropdownMenuProps>(), {
    size: DropdownMenuSize.S,
    trigger: FloatingTrigger.Click,
    placement: FloatingPlacement.Bottom,
    visible: false,
    closable: true,
    fullWidth: false,
    textKey: 'label',
    valueKey: 'value',
    width: '',
    persistent: false,
  });

  const emit = defineEmits<DropdownMenuEmits>();

  const floatingElement = ref(null);

  const onClickOption = (option: MenuOption) => {
    if (!option.disabled) {
      emit('option-click', option[props.valueKey] ?? '');

      if (props.closable) {
        floatingElement.value?.hide();
      }
    }
  };

  const computedWrapperStyles = computed(() => ({ width: props.width }));
  const computedMenuStyles = computed(() => ({ maxHeight: MENU_HEIGHTS[props.size] }));

  defineExpose({
    close: () => floatingElement.value?.hide(),
    open: () => floatingElement.value?.open(),
  });
</script>

<template>
  <Floating
    ref="floatingElement"
    class="tl-dropdown-menu"
    :placement="placement"
    :trigger="trigger"
    :visible="visible"
    :offset="4"
    :full-width="fullWidth"
    :persistent="persistent"
    @change-visible="emit('change-visible', $event)"
    @hide="emit('close')"
  >
    <template #reference>
      <slot name="reference" />
    </template>
    <template #floating>
      <div class="tl-dropdown-menu__wrapper" :style="computedWrapperStyles">
        <slot name="prepend" />
        <Menu
          v-if="options.length > 0"
          class="tl-dropdown-menu__floating"
          :style="computedMenuStyles"
          :size="MENU_SIZE[size]"
          :options="options"
          @option-click="onClickOption"
        >
          <template #option="{ option }">
            <slot name="option" :option="option" />
          </template>
        </Menu>
        <slot name="append" />
      </div>
    </template>
  </Floating>
</template>

<style lang="scss" scoped>
  .tl-dropdown-menu {
    &__wrapper {
      background-color: var(--color-bg-primary);
      border-radius: var(--rounding-s);
      box-shadow: var(--shadow-s);
      position: relative;
      z-index: 5;
    }

    &__floating {
      overflow-y: scroll;
    }
  }
</style>
